import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api, { fmtDate, RATE_META } from "@/lib_app/api";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/app/Avatar";
import DealModal from "@/components/app/DealModal";
import {
  MessageSquareText, PartyPopper, XCircle, Pencil, CalendarCheck, Check, Send, ExternalLink, ArrowRightLeft, Paperclip, Plus, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

const ACTION_META = {
  created:      { label: "Prospek Baru",   icon: Plus,             tint: "bg-[#DCE0E7] text-[#4E6D9A]" },
  won:          { label: "Deal",            icon: PartyPopper,      tint: "bg-[#C9F0D6] text-[#57D28A]" },
  lost:         { label: "Lost",            icon: XCircle,          tint: "bg-[#DCE0E7] text-[#626061]" },
  note:         { label: "Catatan",         icon: MessageSquareText, tint: "bg-[#DCE0E7] text-[#4E6D9A]" },
  stage_change: { label: "Ubah Stage",      icon: ArrowRightLeft,   tint: "bg-[#FFE89A] text-[#B39548]" },
  attachment:   { label: "Lampiran",        icon: Paperclip,        tint: "bg-[#DCE0E7] text-[#4E6D9A]" },
  updated:      { label: "Update Data",     icon: Pencil,           tint: "bg-[#DCE0E7] text-[#626061]" },
  reopened:     { label: "Dibuka Kembali",  icon: RotateCcw,        tint: "bg-teal-100 text-teal-600" },
};

function relTime(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "baru saja";
  if (s < 3600) return `${Math.floor(s / 60)} menit lalu`;
  if (s < 86400) return `${Math.floor(s / 3600)} jam lalu`;
  if (s < 604800) return `${Math.floor(s / 86400)} hari lalu`;
  return fmtDate(iso);
}

export default function NotificationQuickView({ open, prospectId, focusReply = false, replyTo = null, onOpenChange, onAction }) {
  const [prospect, setProspect] = useState(null);
  const [history, setHistory] = useState([]);
  const [users, setUsers] = useState([]);
  const [note, setNote] = useState("");
  const [recipients, setRecipients] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [dealOpen, setDealOpen] = useState(false);
  const [fuJustUpdated, setFuJustUpdated] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !prospectId) return;
    (async () => {
      try {
        const [ps, hs, us] = await Promise.all([
          api.get("/prospects"),
          api.get(`/prospects/${prospectId}/history`),
          users.length ? Promise.resolve({ data: users }) : api.get("/users"),
        ]);
        setProspect(ps.data.find((p) => p.id === prospectId) || null);
        setHistory(hs.data);
        if (!users.length) setUsers(us.data);
      } catch {}
    })();
    setNote("");
    setRecipients(replyTo ? new Set([replyTo]) : new Set());
    setFuJustUpdated(false);
    // eslint-disable-next-line
  }, [open, prospectId, replyTo]);

  const meta = prospect ? RATE_META[prospect.rate] || RATE_META[3] : null;
  const recipientCandidates = useMemo(() => {
    if (!prospect) return [];
    return users.filter((u) => u.is_active !== false && u.role !== "finance" && u.user_id !== user?.user_id && u.user_id !== prospect.owner_id);
  }, [users, user, prospect]);

  const toggleR = (uid) => {
    setRecipients((s) => {
      const n = new Set(s);
      if (n.has(uid)) n.delete(uid); else n.add(uid);
      return n;
    });
  };

  const sendReply = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/prospects/${prospectId}/notes`, { note, recipient_ids: Array.from(recipients) });
      const hs = await api.get(`/prospects/${prospectId}/history`);
      setHistory(hs.data);
      setNote("");
      setRecipients(new Set());
      onAction?.();
      toast.success("Balasan terkirim");
    } catch {
      toast.error("Gagal");
    } finally { setBusy(false); }
  };

  const todayIso = new Date().toISOString().slice(0, 10);
  const fuIsToday = prospect?.last_follow_up === todayIso;
  const canManage = !!prospect && (user?.role === "owner" || prospect.owner_id === user?.user_id);

  const touchFU = async () => {
    setBusy(true);
    try {
      await api.post(`/prospects/${prospectId}/touch_followup`);
      // Refresh both prospect and history to reflect updated FU date
      const [ps, hs] = await Promise.all([
        api.get("/prospects"),
        api.get(`/prospects/${prospectId}/history`),
      ]);
      setProspect(ps.data.find((p) => p.id === prospectId) || null);
      setHistory(hs.data);
      setFuJustUpdated(true);
      setTimeout(() => setFuJustUpdated(false), 4000);
      onAction?.();
      toast.success("Follow-up di-update ke hari ini");
    } catch {
      toast.error("Gagal");
    } finally { setBusy(false); }
  };

  const markLost = async () => {
    if (!confirm("Tandai prospek ini sebagai Lost?")) return;
    setBusy(true);
    try {
      await api.post(`/prospects/${prospectId}/lost`, { reason: "Dari notifikasi" });
      onAction?.();
      toast.success("Ditandai Lost");
      onOpenChange?.(false);
    } catch {
      toast.error("Gagal");
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl rounded-2xl p-0 gap-0 overflow-hidden" data-testid="notif-quickview">
          {!prospect ? (
            <div className="p-8 text-sm text-[#A0A3A8]">Memuat...</div>
          ) : (
            <>
              <DialogHeader className="p-5 pb-4 border-b border-[#DCE0E7]">
                <div className="flex items-start gap-3">
                  <div className={`w-11 h-11 rounded-2xl ${meta.pill} text-white font-bold text-lg flex items-center justify-center shrink-0`}>
                    {meta.letter}
                  </div>
                  <div className="min-w-0 flex-1">
                    <DialogTitle className="text-lg font-bold text-[#090808] truncate">{prospect.customer}</DialogTitle>
                    <div className="text-xs text-[#A0A3A8] truncate">{prospect.service}</div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px]">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold ${meta.soft}`}>{meta.label}</span>
                      <span className="inline-flex items-center gap-1.5 pl-0.5 pr-2 py-0.5 rounded-full bg-[#F4F5F7]">
                        <Avatar name={prospect.owner_name} picture={prospect.owner_picture} size="xs" />
                        <span className="text-[#626061] font-medium">{prospect.owner_nickname || prospect.owner_name}</span>
                      </span>
                      {prospect.last_follow_up && (
                        <span
                          data-testid="quickview-fu-pill"
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium transition-all ${
                            fuJustUpdated
                              ? "bg-[#C9F0D6] text-[#4A6D53] ring-2 ring-emerald-300 animate-pulse"
                              : fuIsToday
                              ? "bg-emerald-50 text-[#57D28A]"
                              : "text-[#A0A3A8]"
                          }`}
                        >
                          <CalendarCheck className="w-3 h-3" />
                          FU {fmtDate(prospect.last_follow_up)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </DialogHeader>

              {/* Timeline */}
              <div className="px-5 py-4 max-h-56 overflow-y-auto" data-testid="notif-quickview-timeline">
                <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold mb-2">Aktivitas Terakhir</div>
                {history.length === 0 ? (
                  <div className="text-xs text-[#A0A3A8]">Belum ada aktivitas.</div>
                ) : (
                  <ul className="space-y-2.5">
                    {history.slice(0, 5).map((h) => {
                      const m = ACTION_META[h.action] || ACTION_META.updated;
                      const Ico = m.icon;
                      return (
                        <li key={h.id} className="flex items-start gap-2.5">
                          <div className={`w-7 h-7 rounded-lg ${m.tint} flex items-center justify-center shrink-0 mt-0.5`}>
                            <Ico className="w-3.5 h-3.5" />
                          </div>
                          <div className="min-w-0 flex-1 text-sm">
                            <div className="text-[#090808]">
                              <span className="font-semibold">{h.actor_name}</span>
                              <span className="text-[#A0A3A8]"> · {m.label}</span>
                            </div>
                            {h.note && (
                              <div className={`text-xs italic mt-0.5 ${h.action === "note" ? "text-[#626061]" : "text-[#A0A3A8]"}`}>
                                &ldquo;{h.note.length > 140 ? h.note.slice(0, 140) + "…" : h.note}&rdquo;
                              </div>
                            )}
                            <div className="text-[10px] text-[#A0A3A8] mt-0.5">{relTime(h.created_at)}</div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Quick reply */}
              <div className="px-5 pt-2 pb-3 border-t border-[#DCE0E7] bg-[#F4F5F7]">
                <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold mb-1.5">Balas Cepat</div>
                <Textarea
                  value={note}
                  autoFocus={focusReply}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Ketik balasan atau update..."
                  rows={2}
                  className="bg-white text-sm"
                  data-testid="quickview-reply-textarea"
                />
                {recipientCandidates.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {recipientCandidates.map((u) => {
                      const active = recipients.has(u.user_id);
                      return (
                        <button
                          key={u.user_id}
                          onClick={() => toggleR(u.user_id)}
                          className={`inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full border text-[11px] transition-colors ${
                            active ? "bg-[#090808] border-[#090808] text-white" : "bg-white border-[#DCE0E7] text-[#626061] hover:border-[#090808]/40"
                          }`}
                        >
                          <Avatar name={u.name} picture={u.picture} size="xs" />
                          <span>{u.nickname || u.name}</span>
                          {active && <Check className="w-2.5 h-2.5" strokeWidth={3} />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Action bar */}
              <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-t border-[#DCE0E7] bg-white">
                <Button
                  onClick={sendReply}
                  disabled={busy || !note.trim()}
                  data-testid="quickview-send-btn"
                  className="bg-[#090808] hover:bg-[#000000] gap-1.5 h-9"
                >
                  <Send className="w-3.5 h-3.5" /> Kirim
                </Button>
                <Button
                  variant="outline"
                  onClick={touchFU}
                  disabled={busy || fuIsToday || !canManage}
                  data-testid="quickview-fu-btn"
                  className={`gap-1.5 h-9 transition-colors ${
                    fuIsToday
                      ? "border-[#C9F0D6] bg-emerald-50 text-[#57D28A] cursor-not-allowed"
                      : "border-[#DCE0E7] text-[#626061] hover:text-[#090808] hover:border-[#090808]/40"
                  } ${!canManage ? "hidden" : ""}`}
                  title={fuIsToday ? "Follow-up sudah di-update hari ini" : "Update follow-up ke hari ini"}
                >
                  {fuIsToday ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : <CalendarCheck className="w-3.5 h-3.5" />}
                  {fuIsToday ? "FU Terkini" : "Update FU"}
                </Button>

                {prospect.status === "active" && canManage && (
                  <>
                    <div className="h-6 w-px bg-[#DCE0E7] mx-0.5" aria-hidden />
                    <Button
                      onClick={() => setDealOpen(true)}
                      disabled={busy}
                      data-testid="quickview-deal-btn"
                      className="gap-1.5 h-9 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-[0_4px_10px_rgba(16,185,129,0.25)] hover:shadow-[0_6px_14px_rgba(16,185,129,0.35)] transition-all"
                    >
                      <PartyPopper className="w-3.5 h-3.5" /> Tandai Deal
                    </Button>
                    <button
                      onClick={markLost}
                      disabled={busy}
                      data-testid="quickview-lost-btn"
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs font-medium text-[#A0A3A8] hover:text-[#FF6B7D] hover:bg-[#F8ECE5] transition-colors disabled:opacity-50"
                      title="Tandai prospek sebagai Lost"
                    >
                      <XCircle className="w-3.5 h-3.5" /> Lost
                    </button>
                  </>
                )}
                {!canManage && (
                  <span className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[11px] font-semibold text-[#A0A3A8] bg-[#F4F5F7]" data-testid="quickview-readonly-badge">
                    <MessageSquareText className="w-3 h-3" /> Balas saja
                  </span>
                )}
                <div className="flex-1" />
                <button
                  onClick={() => { onOpenChange(false); navigate(`/prospects?open=${prospectId}`); }}
                  data-testid="quickview-detail-btn"
                  className="inline-flex items-center gap-1 h-9 px-3 rounded-full text-xs font-semibold text-[#090808] hover:bg-[#F4F5F7] transition-colors"
                >
                  Detail Lengkap <ExternalLink className="w-3 h-3" />
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <DealModal
        prospect={dealOpen ? prospect : null}
        onOpenChange={(o) => !o && setDealOpen(false)}
        onDone={() => { setDealOpen(false); onAction?.(); onOpenChange(false); }}
      />
    </>
  );
}
